import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/auth';
import { ThemeProvider as CustomThemeProvider, useTheme as useCustomTheme } from './contexts/theme';
import { SnackbarProvider } from 'notistack';
import "./i18n";
import { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { alpha, createTheme } from '@mui/material/styles';
import { plPL } from "@mui/x-data-grid/locales";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BreadcrumbsProvider } from './components/ui/breadcrumps/breadcrumbsContext';

const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { isDarkMode } = useCustomTheme();

  const theme = useMemo(() => {
    const backgroundDefault = isDarkMode ? '#0f172a' : '#eef0f5';
    const backgroundPaper = isDarkMode ? '#1e293b' : '#ffffff';

    return createTheme(
      {
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#1976d2' },
          background: {
            default: backgroundDefault,
            paper: backgroundPaper,
          },
          text: {
            primary: isDarkMode ? '#e2e8f0' : '#1a202c',
            secondary: isDarkMode ? '#cbd5e1' : '#4a5568',
          },
        },
        typography: {
          fontFamily:
            "'Atkinson Hyperlegible Next', 'Roboto', 'Helvetica', sans-serif",
        },
        components: {
          MuiAlert: {
            styleOverrides: {
              root: ({ ownerState, theme }) => {
                const severityColor = ownerState.severity === 'error'
                  ? theme.palette.error.main
                  : ownerState.severity === 'warning'
                  ? theme.palette.warning.main
                  : ownerState.severity === 'success'
                  ? theme.palette.success.main
                  : theme.palette.info.main;

                const background = theme.palette.mode === 'dark'
                  ? alpha(severityColor, 0.12)
                  : alpha(severityColor, 0.08);

                const borderColor = theme.palette.mode === 'dark'
                  ? alpha(severityColor, 0.35)
                  : alpha(severityColor, 0.3);

                return {
                  backgroundColor: background,
                  border: `1px solid ${borderColor}`,
                  color: theme.palette.mode === 'dark' ? theme.palette.grey[50] : theme.palette.grey[900],
                  '& .MuiAlert-icon': {
                    color: severityColor,
                  },
                };
              },
            },
          },
        },
      },
      plPL
    );
  }, [isDarkMode]);

  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <CustomThemeProvider>
      <AppThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SnackbarProvider>
            <BrowserRouter>
              <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""} locale="pl">
                <AuthProvider>
                  <BreadcrumbsProvider>
                    <App />
                  </BreadcrumbsProvider>
                </AuthProvider>
              </GoogleOAuthProvider>
            </BrowserRouter>
          </SnackbarProvider>
        </LocalizationProvider>
      </AppThemeProvider>
    </CustomThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
